import { useCampaignSlug } from '@/components/Functional/hooks-core/useCampaignSlug'
import { Header } from '@/components/Layout/Header'
import { SpotDetailSkeleton } from '@/components/Pages/spots/components/detail/SpotDetailSkeleton'
import { useRouter } from 'next/router'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

type Props = {
  /**
   * ボトムナビ。/spots/detail は操作可能な BottomNavigation、/qr は表示専用の BottomNavigationPlaceholder を渡す。
   * このファイルで BottomNavigation を直接 import すると、QR スキャナー（@zxing）等が /qr のバンドルに
   * 巻き込まれるため、呼び出し側から受け取る。
   */
  bottomNavigation: ReactNode
  /**
   * false のとき画面全体を操作不可にする（デフォルト: true）。
   * /qr は遷移先が確定するまでの中継表示で、ヘッダーの戻るボタン等を押されると
   * router.replace と競合して QR の hash が失われるため false を渡す。
   */
  interactive?: boolean
}

/**
 * スポット詳細ページ全体（ヘッダー + 本文 + ボトムナビ）のローディング表示。
 * /spots/detail のローディング中と、/qr（外部カメラ QR の中継ページ, #3587）で共用し、
 * 中継 → 詳細の遷移をまたいでも同じ見た目が続くようにする。
 *
 * NOTE: レイアウトは detail.tsx の本表示と揃えること。
 */
export const SpotDetailPageSkeleton = ({ bottomNavigation, interactive = true }: Props) => {
  const { t } = useTranslation()
  const router = useRouter()
  const { gen } = useCampaignSlug()

  return (
    // h-dvh + main の min-h-0 でスクロールコンテナを main に閉じ込める。
    // 旧実装は height: calc(100dvh - 58px) でヘッダー高さ（44px）を直書きしていた。
    // pb-[72px] は fixed 配置の BottomNavigation（72px）に最終行が隠れないための余白。
    // 旧実装の pb-[60px] は main が 14px 短かった分で帳尻を合わせていたため、他ページと同じ 72px に揃える。
    // inert 未対応の古いブラウザ向けに pointer-events-none も併用する
    <div className={`flex h-dvh flex-col ${interactive ? '' : 'pointer-events-none'}`} inert={!interactive}>
      <Header
        title={t('spot_detail.header_title', 'スポット詳細')}
        showBack
        onBack={() => router.push(gen('/spots'))}
        showNotification={false}
      />
      <main className="min-h-0 flex-1 overflow-y-auto pb-[72px]">
        <SpotDetailSkeleton />
      </main>
      {bottomNavigation}
    </div>
  )
}
